/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestUpcomingMaintenanceInterFace,
  UpcomingMaintenanceInitialProperties,
  UpcomingMaintenanceResponseInterface,
} from '../../interface/upcoming-maintenance-location-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import locationManagementAPI from '../../utils/api/location-management';
// a thunk to fetch users
export const fetchUpcomingMaintenance = createAsyncThunk<
  UpcomingMaintenanceResponseInterface,
  RequestUpcomingMaintenanceInterFace
>(
  'reports-summary/fetchUpcomingMaintenance',
  async (data: RequestUpcomingMaintenanceInterFace, { dispatch }) => {
    try {
      const response = await locationManagementAPI.fetchMaintenanceSchedule(
        removeEmptyKeys({ ...data }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListUpcomingMaintenanceFilters = createAsyncThunk<
  CommonFilterProperties[],
  { chargingStation: string }
>(
  'reports-summary/fetchListUpcomingMaintenanceFilters',
  async ({ chargingStation }, { dispatch }) => {
    if (!chargingStation) {
      throw new Error('chargingStation is required');
    }
    try {
      const response = await locationManagementAPI.fetchMaintenanceScheduleFilters({
        chargingStation,
      });
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
        response.data,
      ).filter((filter) => filter.type !== filterType.DATE_RANGE);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: UpcomingMaintenanceInitialProperties = {
  UpcomingMaintenance: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const UpcomingMaintenanceSlice = createSlice({
  name: 'reports-summary',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing filter update properties.
     */
    updateUpcomingMaintenanceFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableUpcomingMaintenanceFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing sort field and order.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The builder for handling extra reducers.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchUpcomingMaintenance.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUpcomingMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.UpcomingMaintenance = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchUpcomingMaintenance.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListUpcomingMaintenanceFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListUpcomingMaintenanceFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload].filter(
            (object, index, self) =>
              index === self.findIndex((t) => t.key === object.key) &&
              object.type !== filterType.DATE_RANGE,
          );
        },
      )
      .addCase(fetchListUpcomingMaintenanceFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});
// Export the actions
export const {
  updateUpcomingMaintenanceFilters,
  disableUpcomingMaintenanceFiltersApplied,
  setSortData,
} = UpcomingMaintenanceSlice.actions;

// Export the reducer
export default UpcomingMaintenanceSlice.reducer;
